import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { QRCodeSVG } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { 
  faHeart,
  faMusic,
  faHome,
  faCheckCircle,
  faUser,
  faUsers,
  faBank,
  faCreditCard,
  faChevronDown,
  faChevronUp,
  faTimes,
  faRing,
  faChurch,
  faGlassCheers,
  faGift,
  faEnvelope,
  faCamera,
  faImages,
  faCalendarAlt,
  faClock,
  faMapMarkerAlt,
  faLink,
  faCog,
  faFileUpload,
  faCopy,
  faLock,
  faPrint,
  faHotel,
  faMapPin,
  faDownload,
  faExternalLinkAlt,
  faPalette,
  faFont,
  faLocationDot,
  faUpload,
  faEnvelope as faEnvelopeSolid,
  faPhone,
  faUsers as faUsersIcon,
  faChevronLeft,
  faChevronRight,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import * as XLSX from 'xlsx';

const DEFAULT_PRIMARY = '#b87333';
const DEFAULT_ACCENT = '#d4af37';
const DEFAULT_FONT = "'Playfair Display', serif";

// ===== ADMIN SECRET – CHANGE THIS TO YOUR OWN RANDOM STRING =====
const ADMIN_SECRET = 'a7f9k2m5x8q4w1e6r3t0';

const MUSIC_SUGGESTIONS = [
  { label: 'Fairouz – Sahar El Layaly', url: '/fairouz.mp3' },
  { label: 'Piano Instrumental (Soft)', url: 'https://www.bensound.com/bensound-music/bensound-piano-moment.mp3' },
  { label: 'Romantic Strings', url: 'https://www.bensound.com/bensound-music/bensound-romantic.mp3' },
  { label: 'Acoustic Guitar', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-15.mp3' },
  { label: 'Lofi Chill', url: 'https://cdn.pixabay.com/audio/2022/03/10/audio_c8c8f6b6f2.mp3' },
];

function App() {
  // ===== ALL useState hooks =====
  const [style, setStyle] = useState('light');
  const [currentPage, setCurrentPage] = useState(0);
  const [showHamburger, setShowHamburger] = useState(false);
  const totalPages = 10;
  const [uiVisible, setUiVisible] = useState(true);
  const [albumOpen, setAlbumOpen] = useState(false);
  const [selectedAlbumImage, setSelectedAlbumImage] = useState(null);
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [guestView, setGuestView] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [guestTab, setGuestTab] = useState('list');
  const [scaleFactors, setScaleFactors] = useState(Array(totalPages).fill(1));

  // Admin authentication: stored in localStorage
  const [adminAuthenticated, setAdminAuthenticated] = useState(() => {
    return localStorage.getItem('adminAuthenticated') === 'true';
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [guests, setGuests] = useState(() => {
    const saved = localStorage.getItem('guestList');
    return saved ? JSON.parse(saved) : [];
  });

  const [rsvp, setRsvp] = useState({
    name: guestName || '',
    attending: true,
    guests: 1,
    email: ''
  });

  const [confirmationNumber, setConfirmationNumber] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef(null);

  // ===== CONFIG =====
  const getFutureDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const defaultConfig = {
    couple1: 'Bride',
    couple2: 'Groom',
    couple1Family: 'Family1',
    couple2Family: 'Family2',
    eventDate: getFutureDate(),
    ceremonyTime: '15:00',
    ceremonyVenue: 'Your Wedding Venue',
    ceremonyLocation: 'Your City, Country',
    receptionVenue: 'Your Reception Venue',
    receptionLocation: 'Your City, Country',
    cocktailHour: '17:30',
    dinnerTime: '19:00',
    lastDance: '23:30',
    groomHome: 'Your Address, City, Country',
    brideHome: 'Your Address, City, Country',
    bankName: 'Your Bank Name',
    iban: 'Your IBAN Number',
    swift: 'Your SWIFT Code',
    wishMoneyId: 'your-wishmoney-id',
    registryLink: '',
    hotelLink: '',
    accommodations: '',
    musicUrl: '/fairouz.mp3',
    primaryColor: DEFAULT_PRIMARY,
    accentColor: DEFAULT_ACCENT,
    fontFamily: DEFAULT_FONT,
    coverBg: '/pics/cover.jpg',
    invitationBg: '/pics/invitation.jpg',
    ceremonyBg: '/pics/ceremony.jpg',
    churchImg: '/pics/church.jpg',
    celebrationBg: '/pics/celebration.jpg',
    registryBg: '/pics/registry.jpg',
    rsvpBg: '/pics/rsvp.jpg',
    mediaBg: '/pics/media.jpg',
    thankYouBg: '/pics/thankyou.jpg',
    albumImages: []
  };

  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem('weddingConfig');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...defaultConfig, ...parsed };
      } catch (e) {
        return { ...defaultConfig };
      }
    }
    return { ...defaultConfig };
  });

  // ===== useRefs =====
  const uiTimeoutRef = useRef(null);
  const contentRefs = useRef([]);
  const fileInputRef = useRef(null);
  const pointerStartY = useRef(0);
  const pointerStartX = useRef(0);
  const isPointerSwiping = useRef(false);
  const pointerIdRef = useRef(null);
  const wheelTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // ===== Helper functions =====
  const formatDateForFloral = (dateStr, timeStr) => {
    if (!dateStr) return { weekday: 'DATE TBD', date: '', time: 'TIME TBD' };
    try {
      const eventDateObj = new Date(dateStr + 'T' + (timeStr || '12:00'));
      if (isNaN(eventDateObj.getTime())) throw new Error('Invalid date');
      
      const dateOptions = { weekday: 'long', month: 'long', day: 'numeric' };
      const fullDate = eventDateObj.toLocaleDateString('en-US', dateOptions);
      const parts = fullDate.split(', ');
      const weekday = parts[0].toUpperCase();
      const date = parts.slice(1).join(', ').toUpperCase();
      
      const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
      const time = eventDateObj.toLocaleTimeString('en-US', timeOptions).toUpperCase();
      
      return { weekday, date, time };
    } catch (e) {
      return { weekday: 'DATE TBD', date: '', time: 'TIME TBD' };
    }
  };

  const updateCountdown = useCallback(() => {
    if (!config.eventDate || !config.ceremonyTime) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }
    const target = new Date(`${config.eventDate}T${config.ceremonyTime}`);
    const now = Date.now();
    const diff = target - now;
    if (diff <= 0) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }
    setTimeLeft({
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60)
    });
  }, [config.eventDate, config.ceremonyTime]);

  // ===== useCallbacks & useEffects =====
  const resetUiTimer = useCallback(() => {
    setUiVisible(true);
    clearTimeout(uiTimeoutRef.current);
    uiTimeoutRef.current = setTimeout(() => setUiVisible(false), 4000);
  }, []);

  useEffect(() => {
    resetUiTimer();
    return () => clearTimeout(uiTimeoutRef.current);
  }, [resetUiTimer]);

  // ---- Countdown timer ----
  useEffect(() => {
    updateCountdown();
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);
    return () => clearInterval(countdownIntervalRef.current);
  }, [updateCountdown]);

  const recalcScale = useCallback(() => {
    const el = contentRefs.current[currentPage];
    if (!el) return;
    const maxHeight = window.innerHeight * 0.85;
    const maxWidth = window.innerWidth * 0.92;
    const naturalHeight = el.scrollHeight;
    const naturalWidth = el.scrollWidth;
    const scaleX = maxWidth / naturalWidth;
    const scaleY = maxHeight / naturalHeight;
    const newScale = Math.min(1, scaleX, scaleY);
    setScaleFactors(prev => {
      const updated = [...prev];
      updated[currentPage] = newScale;
      return updated;
    });
  }, [currentPage]);

  useEffect(() => {
    recalcScale();
    window.addEventListener('resize', recalcScale);
    return () => window.removeEventListener('resize', recalcScale);
  }, [recalcScale]);

  useEffect(() => {
    recalcScale();
  }, [config, recalcScale]);

  // ---- Guest detection ----
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guestId = params.get('guest');
    const guestNameFromUrl = params.get('guest_name');
    setGuestView(!!guestId);

    if (guestId) {
      const guest = guests.find(g => g.id === guestId);
      if (guest) {
        setGuestName(guest.name);
        if (guest.status === 'pending') {
          const updated = guests.map(g => {
            if (g.id === guestId) return { ...g, status: 'opened' };
            return g;
          });
          setGuests(updated);
          localStorage.setItem('guestList', JSON.stringify(updated));
        }
      } else if (guestNameFromUrl) {
        setGuestName(decodeURIComponent(guestNameFromUrl));
      }
      setIsEditing(false);
    }
  }, [guests]);

  useEffect(() => {
    setRsvp(prev => ({ ...prev, name: guestName }));
  }, [guestName]);

  // ---- Admin login logic ----
  // Show login modal if not authenticated and not in guest view
  useEffect(() => {
    if (!guestView && !adminAuthenticated) {
      setShowLoginModal(true);
    } else {
      setShowLoginModal(false);
    }
  }, [guestView, adminAuthenticated]);

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    if (loginPassword === ADMIN_SECRET) {
      setAdminAuthenticated(true);
      localStorage.setItem('adminAuthenticated', 'true');
      setShowLoginModal(false);
      setLoginError('');
      setLoginPassword('');
    } else {
      setLoginError('Incorrect password. Please try again.');
    }
  };

  // ===== Functions (non‑hook) =====
  const goToPage = (index) => {
    if (index >= 0 && index < totalPages) {
      setCurrentPage(index);
      setTimeout(recalcScale, 50);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
    setTimeout(recalcScale, 50);
  };

  const prevPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
    setTimeout(recalcScale, 50);
  };

  // ----- Pointer events -----
  const handlePointerDown = (e) => {
    if (albumOpen) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pointerIdRef.current = e.pointerId;
    pointerStartY.current = e.clientY;
    pointerStartX.current = e.clientX;
    isPointerSwiping.current = false;
    resetUiTimer();
  };

  const handlePointerMove = (e) => {
    if (albumOpen) return;
    if (e.pointerId !== pointerIdRef.current) return;
    const deltaY = e.clientY - pointerStartY.current;
    const deltaX = e.clientX - pointerStartX.current;
    if (Math.abs(deltaY) > 10 && Math.abs(deltaY) > Math.abs(deltaX)) {
      isPointerSwiping.current = true;
    }
  };

  const handlePointerUp = (e) => {
    if (albumOpen) return;
    if (e.pointerId !== pointerIdRef.current) return;
    if (!isPointerSwiping.current) {
      pointerIdRef.current = null;
      return;
    }
    const deltaY = e.clientY - pointerStartY.current;
    if (Math.abs(deltaY) > 50) {
      if (deltaY < 0) nextPage();
      else prevPage();
    }
    isPointerSwiping.current = false;
    pointerIdRef.current = null;
  };

  // ----- Wheel event -----
  const handleWheel = (e) => {
    if (albumOpen) return;
    if (e.target.closest('.settings-panel') || e.target.closest('.album-modal') || e.target.closest('.hamburger-panel')) return;
    const delta = e.deltaY;
    if (Math.abs(delta) < 20) return;
    if (wheelTimeoutRef.current) return;
    if (delta > 0) nextPage();
    else prevPage();
    resetUiTimer();
    wheelTimeoutRef.current = setTimeout(() => { wheelTimeoutRef.current = null; }, 400);
  };

  // ===== Other functions (save, upload, etc.) =====
  const saveConfig = (newConfig) => {
    setConfig(newConfig);
    try {
      localStorage.setItem('weddingConfig', JSON.stringify(newConfig));
      if (newConfig.eventDate && newConfig.ceremonyTime) {
        const target = new Date(`${newConfig.eventDate}T${newConfig.ceremonyTime}`);
        const diff = target - Date.now();
        if (diff > 0) {
          setTimeLeft({
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60)
          });
        }
      }
    } catch (e) {
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        alert('Storage quota exceeded! Please try using smaller images or deleting an existing background.');
      } else {
        alert('Error saving configuration: ' + e.message);
      }
    }
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...config, [name]: value };
    saveConfig(updated);
  };

  const handleColorChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...config, [name]: value };
    saveConfig(updated);
  };

  const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.4) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
              }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            resolve(compressedDataUrl);
          } catch (e) {
            reject(new Error(`Canvas processing failed: ${e.message || 'Unknown error'}`));
          }
        };
        img.onerror = () => reject(new Error("Failed to load image. The file might be corrupted."));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error("Failed to read file."));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e, key) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedDataUrl = await compressImage(file);
        const updated = { ...config, [key]: compressedDataUrl };
        saveConfig(updated);
      } catch (error) {
        alert(`Error processing image: ${error.message || 'Unknown error. Please try a different file.'}`);
        console.error('Image upload error:', error);
      }
    }
  };

  const handleMusicUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        const updated = { ...config, musicUrl: dataUrl };
        saveConfig(updated);
        alert('Music uploaded successfully! Click the music button to play.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMusicSuggestion = (e) => {
    const url = e.target.value;
    if (url) {
      const updated = { ...config, musicUrl: url };
      saveConfig(updated);
    }
  };

  const resetConfig = () => {
    localStorage.removeItem('weddingConfig');
    setConfig({ ...defaultConfig });
  };

  const generateGuestId = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substring(0, 8);
  };

  const importGuests = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet);
        if (json.length === 0) {
          alert('The file is empty. Please add at least one guest.');
          return;
        }
        const newGuests = json.map(row => {
          const name = row['Name'] || row['name'] || row['Guest Name'] || row['guestName'] || row['NAME'] || 'Guest';
          const phone = String(row['Phone'] || row['phone'] || row['Mobile'] || row['mobile'] || row['PHONE'] || '');
          const email = row['Email'] || row['email'] || row['Guest Email'] || row['EMAIL'] || '';
          return {
            id: generateGuestId(name + phone + email),
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim(),
            status: 'pending',
            rsvpAt: null
          };
        }).filter(g => g.name !== 'Guest' && g.name !== '');
        if (newGuests.length === 0) {
          alert('No valid guests found. Make sure your file has columns: Name, Phone, Email');
          return;
        }
        const updated = [...guests, ...newGuests];
        setGuests(updated);
        localStorage.setItem('guestList', JSON.stringify(updated));
        alert(`Imported ${newGuests.length} guests. Total: ${updated.length}`);
        setGuestTab('list');
      } catch (error) {
        alert('Error reading file. Please make sure it\'s a valid Excel (.xlsx) or CSV file.');
        console.error('Import error:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadSample = () => {
    const sample = [
      { Name: 'John Doe', Phone: '+1234567890', Email: 'john@example.com' },
      { Name: 'Jane Smith', Phone: '+1987654321', Email: 'jane@example.com' }
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sample);
    XLSX.utils.book_append_sheet(wb, ws, 'Guests');
    XLSX.writeFile(wb, 'sample_guest_list.xlsx');
  };

  const exportGuests = () => {
    if (guests.length === 0) {
      alert('No guests to export.');
      return;
    }
    const data = guests.map(g => ({ Name: g.name, Phone: g.phone, Email: g.email, Status: g.status }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Guests');
    XLSX.writeFile(wb, 'guest_list.xlsx');
  };

  const exportForMailMerge = () => {
    if (guests.length === 0) {
      alert('No guests to export.');
      return;
    }
    const baseUrl = window.location.origin + window.location.pathname;
    const data = guests.map(g => ({
      'First Name': g.name,
      'Email': g.email,
      'Invitation Link': `${baseUrl}?guest=${g.id}`
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Mail Merge');
    XLSX.writeFile(wb, 'mail_merge_guests.xlsx');
    alert(`✅ Exported ${guests.length} guests for mail merge.`);
  };

  const sendInvitations = () => {
    if (guests.length === 0) {
      alert('No guests to send invitations to.');
      return;
    }
    const baseUrl = window.location.origin + window.location.pathname;
    const links = guests.map(g => `${g.name}: ${baseUrl}?guest=${g.id}`).join('\n');
    navigator.clipboard.writeText(links).then(() => {
      alert(`✅ Copied ${guests.length} guest links to clipboard!\n\nPaste them into WhatsApp, email, or any messaging app to send invitations.`);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = links;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert(`✅ Copied ${guests.length} guest links to clipboard!\n\nPaste them into WhatsApp, email, or any messaging app to send invitations.`);
    });
  };

  const handleGuestRsvp = (guestId, status) => {
    const updatedGuests = guests.map(g => {
      if (g.id === guestId) {
        return { ...g, status, rsvpAt: new Date().toISOString() };
      }
      return g;
    });
    setGuests(updatedGuests);
    localStorage.setItem('guestList', JSON.stringify(updatedGuests));
    alert(`Thank you! You've RSVP'd "${status}".`);
  };

  const handleRsvpChange = (e) => {
    const target = e.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setRsvp({ ...rsvp, [target.name]: value });
  };

  const handleRsvpSubmit = (e) => {
    e.preventDefault();
    const newCode = 'WED-' + Math.random().toString(36).substring(2, 8).toUpperCase();
    setConfirmationNumber(newCode);
    setShowConfirmation(true);
    setTimeout(() => {
      setShowConfirmation(false);
      goToPage(9);
    }, 2000);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024).toFixed(1),
      url: URL.createObjectURL(file)
    }));
    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const printInvitation = () => {
    const printWindow = window.open('', '_blank');
    const baseUrl = window.location.origin + window.location.pathname;
    const qrValue = baseUrl + '?guest=' + (guestName || 'guest');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Wedding Invitation</title>
      <style>
        body { font-family: 'Georgia', serif; max-width: 600px; margin: 40px auto; padding: 20px; text-align: center; color: #3d2c1e; }
        .card { border: 1px solid #d4c8b8; padding: 40px; border-radius: 12px; background: #fff; }
        h1 { font-size: 36px; margin-bottom: 4px; }
        h2 { font-size: 28px; color: #b87333; margin-bottom: 20px; }
        .details { margin: 20px 0; font-size: 16px; color: #6b5e50; }
        .qr { margin: 20px 0; }
        .footer { margin-top: 20px; font-size: 14px; color: #a89888; }
        hr { border: none; border-top: 1px solid #e0d5c8; margin: 20px 0; }
      </style>
      </head><body>
      <div class="card">
        <h1>${config.couple1} & ${config.couple2}</h1>
        <h2>WE ARE GETTING MARRIED</h2>
        <div class="details">
          <p>${config.eventDate} at ${config.ceremonyTime}</p>
          <p>${config.ceremonyVenue}, ${config.ceremonyLocation}</p>
          ${config.receptionVenue ? `<p>Reception: ${config.receptionVenue}, ${config.receptionLocation}</p>` : ''}
        </div>
        <hr />
        <p><strong>RSVP</strong><br />Scan the QR code below to respond</p>
        <div class="qr">
          <img src="https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrValue)}&size=150x150&color=b87333" alt="QR Code" />
        </div>
        <div class="footer">${baseUrl}?guest=yourname</div>
        <hr />
        <p style="font-size:12px; color:#999;">This is a digital invitation. Please scan the QR code to RSVP online.</p>
      </div>
      </body></html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isMusicPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => console.log('Autoplay blocked:', err));
      }
      setIsMusicPlaying(!isMusicPlaying);
    }
  };

  const shareWhatsApp = () => {
    const link = window.location.href + (window.location.href.includes('?') ? '&' : '?') + 't=' + Date.now();
    window.open(`https://wa.me/?text=${encodeURIComponent(`Dear ${guestName || 'Guest'},\nYou're invited to ${config.couple1} & ${config.couple2}'s wedding! ${link}`)}`, '_blank');
  };

  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent('Wedding Invitation')}&body=${encodeURIComponent(`Dear ${guestName || 'Guest'},\n\nYou're invited to ${config.couple1} & ${config.couple2}'s wedding!\n\n${window.location.href}`)}`;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => alert('Link copied!'))
      .catch(() => prompt('Copy this link:', window.location.href));
  };

  const handleHeartSpawn = () => {};

  const openLocationPicker = (field, currentValue) => {
    const searchQuery = currentValue || '';
    window.open(`https://www.google.com/maps/search/${encodeURIComponent(searchQuery)}`, '_blank');
  };

  // ===== Menu items =====
  const menuItems = [
    { label: 'Classic Invitation', page: 1 },
    { label: 'Wedding Ceremony', page: 2 },
    { label: 'Wedding Celebration', page: 3 },
    { label: 'Locations', page: 4 },
    { label: 'Gift Registry', page: 5 },
    { label: 'RSVP', page: 6 },
    { label: 'Media Upload', page: 7 },
    { label: 'Our Album', page: 8 },
    { label: 'Thank You', page: 9 }
  ];

  // ===== Render functions =====
  const renderHamburgerMenu = () => (
    <div className="hamburger-overlay" onClick={() => setShowHamburger(false)}>
      <div className="hamburger-panel" onClick={(e) => e.stopPropagation()}>
        <button className="hamburger-close" onClick={() => setShowHamburger(false)}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h3 className="hamburger-title">Navigate</h3>
        <div className="hamburger-list">
          {menuItems.map((item, idx) => (
            <div 
              key={idx} 
              className={`hamburger-item ${currentPage === item.page ? 'active' : ''}`}
              onClick={() => { goToPage(item.page); setShowHamburger(false); }}
            >
              <span>{item.label}</span>
              {currentPage === item.page && <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--accent-color)' }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAlbumPage = () => {
    const bg = config.mediaBg ? `url(${config.mediaBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    const images = config.albumImages && config.albumImages.length > 0
      ? config.albumImages
      : [config.coverBg, config.invitationBg, config.ceremonyBg, config.churchImg, config.celebrationBg, config.registryBg, config.rsvpBg, config.mediaBg, config.thankYouBg].filter(Boolean);

    return (
      <div className="story-page album-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content album-page-content" ref={el => contentRefs.current[8] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">Our Album</h2>
            {images.length === 0 ? (
              <p style={{color:'var(--text-secondary)'}}>No photos uploaded yet. Upload photos in the settings panel.</p>
            ) : (
              <div className="album-page-grid">
                {images.slice(0, 6).map((img, i) => (
                  <div key={i} className="album-page-item" onClick={() => { setSelectedAlbumImage(img); setAlbumOpen(true); }}>
                    <img src={img} alt={`Album ${i}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderClassicInvitation = () => {
    const { weekday, date, time } = formatDateForFloral(config.eventDate, config.ceremonyTime);
    const name1 = config.couple1 || '';
    const name2 = config.couple2 || '';
    const family1 = config.couple1Family || '';
    const family2 = config.couple2Family || '';

    return (
      <div className="story-page floral-invitation-page" style={{ 
        backgroundImage: config.invitationBg ? `url(${config.invitationBg})` : 'linear-gradient(135deg, #fdfbf7, #f2efe9)',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="floral-invitation-content" ref={el => contentRefs.current[1] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <p className="floral-small-text">YOU ARE CORDIALLY</p>
            <p className="floral-small-text">INVITED TO THE</p>
            
            <h3 className="page-title" style={{ fontSize: '28px', margin: '20px 0 10px 0', color: 'var(--text-primary)' }}>WEDDING OF</h3>
            
            <div className="cover-names" style={{ fontSize: '36px', fontWeight: '700', margin: '8px 0', fontFamily: config.fontFamily || DEFAULT_FONT }}>
              {`${name1} ${family1}`.toUpperCase()}
            </div>
            
            <div className="classic-separator" style={{ margin: '10px 0', color: config.accentColor || DEFAULT_ACCENT, letterSpacing: '8px', fontSize: '24px' }}>&</div>
            
            <div className="cover-names" style={{ fontSize: '36px', fontWeight: '700', margin: '8px 0', fontFamily: config.fontFamily || DEFAULT_FONT }}>
              {`${name2} ${family2}`.toUpperCase()}
            </div>
            
            <div className="floral-date-time" style={{ marginTop: '32px', fontSize: '16px', color: 'var(--text-secondary)' }}>
              {weekday},<br />
              {date}
            </div>
            
            <div className="floral-date-time" style={{ marginTop: '4px', fontSize: '16px', color: 'var(--text-secondary)' }}>
              AT {time}
            </div>
            
            <div className="floral-location" style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: '0', fontSize: '14px', fontWeight: '600' }}>{config.receptionVenue || 'Venue TBD'}</p>
            </div>
            
            <div className="floral-script-footer" style={{ marginTop: '24px', fontSize: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
              Let's celebrate with love and joy
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCover = () => {
    const bg = config.coverBg ? `url(${config.coverBg})` : `linear-gradient(135deg, #2a1f1a, #1a1410)`;
    return (
      <div className="story-page cover-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="cover-content" ref={el => contentRefs.current[0] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <div className="cover-subtitle">WE ARE GETTING MARRIED</div>
            <h1 className="cover-names" style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
              {config.couple1} <span className="cover-amp">&</span> {config.couple2}
            </h1>
            <div className="cover-greeting">
              <FontAwesomeIcon icon={faHeart} className="greeting-icon" />
              <span>Dear {guestName},</span>
            </div>
            <div className="cover-countdown">
              <div className="countdown-item">
                <span className="countdown-number">{String(timeLeft.days).padStart(2, '0')}</span>
                <span className="countdown-label">Days</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-number">{String(timeLeft.hours).padStart(2, '0')}</span>
                <span className="countdown-label">Hours</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-number">{String(timeLeft.minutes).padStart(2, '0')}</span>
                <span className="countdown-label">Minutes</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-number">{String(timeLeft.seconds).padStart(2, '0')}</span>
                <span className="countdown-label">Seconds</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCeremony = () => {
    const bg = config.ceremonyBg ? `url(${config.ceremonyBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page ceremony-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content" ref={el => contentRefs.current[2] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">Wedding Ceremony</h2>
            {config.churchImg && (
              <div className="ceremony-top-image">
                <img src={config.churchImg} alt="Church" />
              </div>
            )}
            <div className="ceremony-details">
              <div className="ceremony-date">
                <span className="ceremony-day"><FontAwesomeIcon icon={faCalendarAlt} /> {config.eventDate ? new Date(config.eventDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase() : 'DATE TBD'}</span>
                <span className="ceremony-time"><FontAwesomeIcon icon={faClock} /> AT {config.ceremonyTime || 'TBD'}</span>
              </div>
              <div className="ceremony-venue">
                <h3><FontAwesomeIcon icon={faChurch} /> {config.ceremonyVenue}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <a 
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.ceremonyVenue + ', ' + config.ceremonyLocation)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="open-maps-btn"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in Google Maps
                  </a>
                  {adminAuthenticated && (
                    <button 
                      className="open-maps-btn" 
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      onClick={() => openLocationPicker('ceremonyLocation', config.ceremonyLocation)}
                    >
                      <FontAwesomeIcon icon={faLocationDot} /> Pick Location
                    </button>
                  )}
                </div>
              </div>
              <div className="ceremony-map">
                <iframe 
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(config.ceremonyVenue + ', ' + config.ceremonyLocation)}`}
                  width="100%" 
                  height="150" 
                  style={{border:0, borderRadius: '12px'}} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Ceremony Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCelebration = () => {
    const bg = config.celebrationBg ? `url(${config.celebrationBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page celebration-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content" ref={el => contentRefs.current[3] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">Wedding Celebration</h2>
            <div className="celebration-timeline">
              {[
                { time: config.cocktailHour, event: 'Cocktail Hour', desc: 'Drinks & Canapés' },
                { time: config.dinnerTime, event: 'Dinner & Reception', desc: 'Three-course meal & dancing' },
                { time: config.lastDance, event: 'Last Dance', desc: 'To new beginnings' },
              ].map((item, idx) => (
                <div key={idx} className="timeline-item">
                  <span className="timeline-time"><FontAwesomeIcon icon={faClock} /> {item.time}</span>
                  <span className="timeline-event">{item.event}</span>
                  <span className="timeline-desc">{item.desc}</span>
                </div>
              ))}
            </div>
            <div className="celebration-venue">
              <p className="venue-label">THE RECEPTION & THE CELEBRATION</p>
              <p className="venue-name">WILL TAKE PLACE AT</p>
              <h3><FontAwesomeIcon icon={faGlassCheers} /> {config.receptionVenue}</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <a 
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.receptionVenue + ', ' + config.receptionLocation)}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="open-maps-btn"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in Google Maps
                </a>
                {adminAuthenticated && (
                  <button 
                    className="open-maps-btn" 
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => openLocationPicker('receptionLocation', config.receptionLocation)}
                  >
                    <FontAwesomeIcon icon={faLocationDot} /> Pick Location
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLocations = () => {
    const bg = config.coverBg ? `url(${config.coverBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page locations-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content locations-content" ref={el => contentRefs.current[4] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">Getting Ready Locations</h2>
            <div className="locations-grid">
              {[
                { title: "Groom's Home", address: config.groomHome, field: 'groomHome' },
                { title: "Bride's Home", address: config.brideHome, field: 'brideHome' },
              ].map((loc, idx) => (
                <div key={idx} className="location-card">
                  <h3 className="location-title"><FontAwesomeIcon icon={faMapMarkerAlt} /> {loc.title}</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <a 
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc.address)}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="open-maps-btn"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in Google Maps
                    </a>
                    {adminAuthenticated && (
                      <button 
                        className="open-maps-btn" 
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        onClick={() => openLocationPicker(loc.field, loc.address)}
                      >
                        <FontAwesomeIcon icon={faLocationDot} /> Pick Location
                      </button>
                    )}
                  </div>
                  <div className="location-map">
                    <iframe 
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(loc.address)}`}
                      width="100%" 
                      height="200" 
                      style={{border:0, borderRadius: '16px'}} 
                      allowFullScreen="" 
                      loading="lazy"
                      title={`${loc.title} Location`}
                    ></iframe>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGiftRegistry = () => {
    const bg = config.registryBg ? `url(${config.registryBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page registry-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content" ref={el => contentRefs.current[5] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">Gift Registry</h2>
            <div className="registry-message">
              <p className="registry-intro">A THOUGHTFUL GESTURE</p>
              <p className="registry-text">Your presence is the greatest gift of all.</p>
              <p className="registry-text">If you wish to bless us further, you can do so in the following ways:</p>
            </div>
            <div className="registry-details">
              <div className="registry-item">
                <span className="registry-label"><FontAwesomeIcon icon={faBank} /> {config.bankName}</span>
                <span className="registry-value">IBAN: {config.iban}</span>
                <span className="registry-value-small">SWIFT CODE: {config.swift}</span>
              </div>
              <div className="registry-item">
                <span className="registry-label"><FontAwesomeIcon icon={faCreditCard} /> WISH MONEY</span>
                <span className="registry-value">ACCOUNT ID: {config.wishMoneyId}</span>
              </div>
              {config.registryLink && (
                <div className="registry-item">
                  <span className="registry-label"><FontAwesomeIcon icon={faGift} /> Online Registry</span>
                  <span className="registry-value"><a href={config.registryLink} target="_blank" rel="noopener noreferrer">{config.registryLink}</a></span>
                </div>
              )}
              {config.hotelLink && (
                <div className="registry-item">
                  <span className="registry-label"><FontAwesomeIcon icon={faHotel} /> Hotel Booking</span>
                  <span className="registry-value"><a href={config.hotelLink} target="_blank" rel="noopener noreferrer">{config.hotelLink}</a></span>
                </div>
              )}
              {config.accommodations && (
                <div className="registry-item">
                  <span className="registry-label"><FontAwesomeIcon icon={faMapPin} /> Accommodations</span>
                  <span className="registry-value">{config.accommodations}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRsvp = () => {
    const bg = config.rsvpBg ? `url(${config.rsvpBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page rsvp-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content" ref={el => contentRefs.current[6] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">RSVP</h2>
            <div className="rsvp-container">
              {showConfirmation ? (
                <div className="rsvp-confirmation">
                  <FontAwesomeIcon icon={faCheckCircle} className="confirmation-icon" />
                  <h3>You're Confirmed!</h3>
                  <p>Your confirmation number:</p>
                  <div className="confirmation-code">{confirmationNumber}</div>
                  <p style={{marginTop: '10px', fontSize: '14px'}}>
                    We'll send a copy to {rsvp.email}
                  </p>
                </div>
              ) : (
                <form className="rsvp-form" onSubmit={handleRsvpSubmit}>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px' }} /> Guest
                    </label>
                    <div style={{ 
                      padding: '12px', 
                      borderRadius: '10px', 
                      background: 'var(--input-bg)', 
                      color: 'var(--text-primary)', 
                      fontSize: '16px', 
                      fontWeight: '600',
                      border: '1px solid var(--border-color)'
                    }}>
                      {guestName || 'Guest'}
                    </div>
                    <small className="guest-hint" style={{ display: 'block', marginTop: '6px', color: 'var(--text-secondary)' }}>
                      This RSVP is personalized for you.
                    </small>
                  </div>

                  <div className="rsvp-attending">
                    <label className="attending-label">
                      <input 
                        type="radio" 
                        name="attending" 
                        value="true" 
                        checked={rsvp.attending === true} 
                        onChange={() => setRsvp({...rsvp, attending: true})} 
                      /> Yes, I'll be there
                    </label>
                    <label className="attending-label">
                      <input 
                        type="radio" 
                        name="attending" 
                        value="false" 
                        checked={rsvp.attending === false} 
                        onChange={() => setRsvp({...rsvp, attending: false})} 
                      /> Sorry, can't attend
                    </label>
                  </div>

                  <div className="form-group">
                    <label><FontAwesomeIcon icon={faUsers} /> Number of Guests</label>
                    <select 
                      name="guests" 
                      value={rsvp.guests} 
                      onChange={handleRsvpChange}
                      required
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: '1px solid var(--border-color)', 
                        borderRadius: '10px', 
                        background: 'var(--input-bg)', 
                        color: 'var(--text-primary)', 
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label><FontAwesomeIcon icon={faEnvelope} /> Your Email</label>
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Enter your email" 
                      value={rsvp.email} 
                      onChange={handleRsvpChange} 
                      required 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: '1px solid var(--border-color)', 
                        borderRadius: '10px', 
                        background: 'var(--input-bg)', 
                        color: 'var(--text-primary)', 
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <button type="submit" className="rsvp-submit">
                    Confirm RSVP
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMedia = () => {
    const bg = config.mediaBg ? `url(${config.mediaBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page media-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content" ref={el => contentRefs.current[7] = el} style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>
            <h2 className="page-title">Media Upload</h2>
            <div className="media-content">
              <p className="media-intro">Our day through your eyes</p>
              <p className="media-text">We would love to see the memories you captured—please share your photos and videos with us.</p>
              
              <div className="upload-area" onClick={triggerFileInput}>
                <FontAwesomeIcon icon={faCamera} className="upload-icon" />
                <span>Choose files</span>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileUpload} 
                  multiple 
                  accept="image/*,video/*" 
                  style={{display: 'none'}} 
                />
              </div>
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  {uploadedFiles.map(file => (
                    <div key={file.id} className="uploaded-file">
                      <span>📎 {file.name}</span>
                      <span className="file-size">{file.size} KB</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderThankYou = () => {
    const bg = config.thankYouBg ? `url(${config.thankYouBg})` : `linear-gradient(135deg, #f5f0eb, #e8e0d8)`;
    return (
      <div className="story-page thankyou-page" style={{ backgroundImage: bg }}>
        <div className="card-wrapper" style={{ transform: `scale(${scaleFactors[currentPage]})`, transformOrigin: 'center center' }}>
          <div className="page-content thankyou-content" ref={el => contentRefs.current[9] = el}>
            <FontAwesomeIcon icon={faRing} className="thankyou-icon" />
            <h2 className="thankyou-title" style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>Thank you</h2>
            <p className="thankyou-text">For joining us on this special day.</p>
            <p className="thankyou-text">Your presence will light the night sky:</p>
            <p className="thankyou-couple" style={{ fontFamily: config.fontFamily || DEFAULT_FONT }}>{config.couple1} <FontAwesomeIcon icon={faHeart} /> {config.couple2}</p>
            <div className="thankyou-year">2026</div>
          </div>
        </div>
      </div>
    );
  };

  const closeAlbum = () => {
    setAlbumOpen(false);
    setSelectedAlbumImage(null);
  };

  const renderAlbumModal = () => {
    const albumImages = config.albumImages && config.albumImages.length > 0
      ? config.albumImages
      : [config.coverBg, config.invitationBg, config.ceremonyBg, config.churchImg, config.celebrationBg, config.registryBg, config.rsvpBg, config.mediaBg, config.thankYouBg].filter(Boolean);

    if (albumImages.length === 0) {
      return (
        <div className="album-modal" onClick={closeAlbum}>
          <div className="album-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="album-close" onClick={closeAlbum}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 className="album-modal-title">Our Album</h2>
            <p style={{textAlign:'center'}}>No photos uploaded yet. Upload photos in the settings panel.</p>
          </div>
        </div>
      );
    }

    const handleNext = () => {
      if (!selectedAlbumImage) return;
      const currentIdx = albumImages.indexOf(selectedAlbumImage);
      const nextIdx = (currentIdx + 1) % albumImages.length;
      setSelectedAlbumImage(albumImages[nextIdx]);
    };

    const handlePrev = () => {
      if (!selectedAlbumImage) return;
      const currentIdx = albumImages.indexOf(selectedAlbumImage);
      const prevIdx = (currentIdx - 1 + albumImages.length) % albumImages.length;
      setSelectedAlbumImage(albumImages[prevIdx]);
    };

    if (selectedAlbumImage) {
      return (
        <div className="album-modal" onClick={closeAlbum}>
          <div className="album-modal-content album-enlarged-content" onClick={(e) => e.stopPropagation()}>
            <button className="album-close" onClick={closeAlbum}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <div className="album-enlarged-container">
              <img src={selectedAlbumImage} alt="Enlarged Album" className="album-enlarged-image" />
              
              <div className="album-nav-buttons">
                <button className="album-nav-btn" onClick={(e) => { e.stopPropagation(); handlePrev(); }}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button className="album-nav-btn" onClick={(e) => { e.stopPropagation(); handleNext(); }}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
            
            <div className="album-index-indicator">
              {albumImages.indexOf(selectedAlbumImage) + 1} / {albumImages.length}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="album-modal" onClick={closeAlbum}>
        <div className="album-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="album-close" onClick={closeAlbum}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <h2 className="album-modal-title">Our Album</h2>
          <div className="album-modal-grid">
            {albumImages.map((img, idx) => (
              <div key={idx} className="album-modal-photo" onClick={() => setSelectedAlbumImage(img)}>
                <img src={img} alt={`Photo ${idx+1}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <div className="settings-overlay" onClick={() => setShowSettings(false)}>
        <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
          <button className="settings-close" onClick={() => setShowSettings(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <h2 className="settings-title">Customize & Manage</h2>

          <div className="settings-tabs">
            <button className={`settings-tab ${guestTab === 'list' ? 'active' : ''}`} onClick={() => setGuestTab('list')}>
              <FontAwesomeIcon icon={faUsers} /> Guests
            </button>
            <button className={`settings-tab ${guestTab === 'import' ? 'active' : ''}`} onClick={() => setGuestTab('import')}>
              <FontAwesomeIcon icon={faFileUpload} /> Import
            </button>
            <button className={`settings-tab ${guestTab === 'details' ? 'active' : ''}`} onClick={() => setGuestTab('details')}>
              <FontAwesomeIcon icon={faCog} /> Details
            </button>
            <button className={`settings-tab ${guestTab === 'colors' ? 'active' : ''}`} onClick={() => setGuestTab('colors')}>
              <FontAwesomeIcon icon={faPalette} /> Colors & Fonts
            </button>
          </div>
          <div className="settings-scroll">
            {guestTab === 'list' && (
              <div className="settings-section">
                <div className="flex-between">
                  <h3>Guest List ({guests.length})</h3>
                  <div>
                    <button className="btn-sm btn-secondary" onClick={exportGuests}>
                      <FontAwesomeIcon icon={faDownload} /> Export
                    </button>
                    <button className="btn-sm btn-primary" onClick={sendInvitations}>
                      <FontAwesomeIcon icon={faWhatsapp} /> Send Invitations
                    </button>
                    <button className="btn-sm btn-success" onClick={exportForMailMerge}>
                      <FontAwesomeIcon icon={faEnvelopeSolid} /> Export Mail Merge
                    </button>
                  </div>
                </div>
                <div className="guest-table-wrap">
                  <table className="guest-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>WhatsApp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guests.length === 0 ? (
                        <tr><td colSpan="6" style={{textAlign:'center'}}>No guests imported.</td></tr>
                      ) : (
                        guests.map((g, i) => (
                          <tr key={g.id}>
                            <td>{i+1}</td>
                            <td>{g.name}</td>
                            <td>{g.phone || '-'}</td>
                            <td>{g.email || '-'}</td>
                            <td><span className={`status-badge ${g.status}`}>{g.status}</span></td>
                            <td>
                              {g.phone && (
                                <button
                                  className="btn-sm btn-whatsapp"
                                  onClick={() => {
                                    const baseUrl = window.location.origin + window.location.pathname;
                                    const fullUrl = `${baseUrl}?guest=${g.id}&t=${Date.now()}`;
                                    const message = `Hi ${g.name}, you're invited to ${config.couple1} & ${config.couple2}'s wedding! Click here to view your invitation and RSVP: ${fullUrl}`;
                                    const url = `https://wa.me/${g.phone.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
                                    window.open(url, '_blank');
                                  }}
                                  title="Send WhatsApp invitation"
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                                >
                                  <FontAwesomeIcon icon={faWhatsapp} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-16">
                  <h4 style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>💾 Data Safety</h4>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button 
                      className="btn-sm btn-success" 
                      onClick={() => {
                        const data = { weddingConfig: config, guestList: guests };
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `wedding_backup_${new Date().toISOString().slice(0,10)}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <FontAwesomeIcon icon={faDownload} /> Backup Data
                    </button>
                    <label className="btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faUpload} /> Restore Data
                      <input 
                        type="file" 
                        accept=".json" 
                        style={{ display: 'none' }} 
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            try {
                              const data = JSON.parse(event.target.result);
                              if (data.weddingConfig) saveConfig(data.weddingConfig);
                              if (data.guestList) {
                                setGuests(data.guestList);
                                localStorage.setItem('guestList', JSON.stringify(data.guestList));
                              }
                              alert('✅ Data restored successfully!');
                            } catch (error) {
                              alert('❌ Invalid backup file.');
                            }
                          };
                          reader.readAsText(file);
                        }}
                      />
                    </label>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-subtle)', marginTop: '8px' }}>
                    Backup your full configuration and guest list. If you clear your browser cache, you can restore everything instantly.
                  </p>
                </div>

                <div className="mt-16 flex-between">
                  <button className="btn btn-secondary" onClick={printInvitation}>
                    <FontAwesomeIcon icon={faPrint} /> Print Invitation
                  </button>
                </div>
              </div>
            )}
            {guestTab === 'import' && (
              <div className="settings-section">
                <h3>Import Guests</h3>
                <p>Upload an Excel (.xlsx) or CSV file with columns: <strong>Name</strong>, <strong>Phone</strong>, <strong>Email</strong> (optional).</p>
                <div className="import-area">
                  <input 
                    type="file" 
                    accept=".xlsx,.xls,.csv" 
                    id="guestFileInput"
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        importGuests(e.target.files[0]);
                        e.target.value = '';
                      }
                    }} 
                  />
                  <button className="btn btn-primary" onClick={() => document.getElementById('guestFileInput').click()}>
                    <FontAwesomeIcon icon={faFileUpload} /> Choose File
                  </button>
                </div>
                <p style={{fontSize:'12px', marginTop:'8px'}}>
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    downloadSample();
                  }}>📥 Download sample Excel template</a>
                </p>
              </div>
            )}
            {guestTab === 'details' && (
              <div className="settings-section">
                <h3>Event Details</h3>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Couple 1 First Name</label>
                    <input type="text" name="couple1" value={config.couple1 || ''} onChange={handleConfigChange} />
                  </div>
                  <div className="form-group">
                    <label>Couple 1 Family Name</label>
                    <input type="text" name="couple1Family" value={config.couple1Family || ''} onChange={handleConfigChange} />
                  </div>
                  <div className="form-group">
                    <label>Couple 2 First Name</label>
                    <input type="text" name="couple2" value={config.couple2 || ''} onChange={handleConfigChange} />
                  </div>
                  <div className="form-group">
                    <label>Couple 2 Family Name</label>
                    <input type="text" name="couple2Family" value={config.couple2Family || ''} onChange={handleConfigChange} />
                  </div>

                  {Object.keys(defaultConfig).filter(k => 
                    !k.includes('Bg') && 
                    k !== 'albumImages' && 
                    k !== 'registryLink' && 
                    k !== 'hotelLink' && 
                    k !== 'accommodations' && 
                    k !== 'musicUrl' && 
                    k !== 'primaryColor' && 
                    k !== 'accentColor' && 
                    k !== 'fontFamily' && 
                    k !== 'churchImg' && 
                    k !== 'eventDate' && 
                    k !== 'ceremonyTime' &&
                    k !== 'couple1' && k !== 'couple2' &&
                    k !== 'couple1Family' && k !== 'couple2Family'
                  ).map(key => (
                    <div className="form-group" key={key}>
                      <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                      <input type="text" name={key} value={config[key] || ''} onChange={handleConfigChange} />
                    </div>
                  ))}
                  <div className="form-group">
                    <label>Event Date</label>
                    <input type="date" name="eventDate" value={config.eventDate || ''} onChange={handleConfigChange} />
                  </div>
                  <div className="form-group">
                    <label>Ceremony Time</label>
                    <input type="time" name="ceremonyTime" value={config.ceremonyTime || ''} onChange={handleConfigChange} />
                  </div>
                </div>
                <div className="mt-16">
                  <h4 style={{marginBottom:'8px'}}>Extra Links</h4>
                  <div className="form-group">
                    <label>Registry Link</label>
                    <input type="text" name="registryLink" value={config.registryLink || ''} onChange={handleConfigChange} placeholder="https://..." />
                  </div>
                  <div className="form-group">
                    <label>Hotel Booking Link</label>
                    <input type="text" name="hotelLink" value={config.hotelLink || ''} onChange={handleConfigChange} placeholder="https://..." />
                  </div>
                  <div className="form-group">
                    <label>Accommodations Info</label>
                    <input type="text" name="accommodations" value={config.accommodations || ''} onChange={handleConfigChange} placeholder="e.g. Hilton Hotel, use code WED2026" />
                  </div>
                </div>
                <div className="mt-16">
                  <h4 style={{marginBottom:'8px'}}>Music</h4>
                  <div className="form-group">
                    <label>Choose a song</label>
                    <select 
                      name="musicSuggestion" 
                      onChange={handleMusicSuggestion}
                      style={{padding:'10px', borderRadius:'10px', border:'1px solid #d4c8b8', width:'100%', fontFamily:'inherit'}}
                    >
                      <option value="">-- Select a song --</option>
                      {MUSIC_SUGGESTIONS.map((song, idx) => (
                        <option key={idx} value={song.url}>{song.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Or upload your own MP3</label>
                    <div className="image-upload-row">
                      <input type="file" accept="audio/*" onChange={handleMusicUpload} />
                      {config.musicUrl && <span className="upload-status">✅ Uploaded</span>}
                    </div>
                    <small style={{color:'#999'}}>Upload an MP3 file (max 5MB). It will be saved with your invitation.</small>
                  </div>
                  <div className="form-group">
                    <label>Or paste a direct URL</label>
                    <input type="text" name="musicUrl" value={config.musicUrl || ''} onChange={handleConfigChange} placeholder="https://example.com/music.mp3" />
                  </div>
                </div>
                <div className="mt-16">
                  <h4 style={{marginBottom:'8px'}}>Upload Photos</h4>
                  <p style={{fontSize:'12px', color:'#999', marginBottom:'8px'}}>Upload each image – the app will use them as backgrounds. Leave blank for solid color.</p>
                  <div className="grid-2">
                    {['coverBg','invitationBg','ceremonyBg','churchImg','celebrationBg','registryBg','rsvpBg','mediaBg','thankYouBg'].map((key) => (
                      <div className="form-group" key={key}>
                        <label>{key.replace('Bg','').replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                        <div className="image-upload-row">
                          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, key)} />
                          {config[key] && <span className="upload-status">✅ Uploaded</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <label>Album Images (upload up to 6 photos)</label>
                    <div className="image-upload-row">
                      <input type="file" accept="image/*" multiple 
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          try {
                            const dataUrls = await Promise.all(files.map(file => compressImage(file)));
                            const updated = { ...config, albumImages: dataUrls };
                            saveConfig(updated);
                          } catch (error) {
                            alert(`Error processing album images: ${error.message || 'Unknown error'}`);
                          }
                        }}
                      />
                      {config.albumImages && config.albumImages.length > 0 && <span className="upload-status">✅ {config.albumImages.length} uploaded</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-16">
                  <button className="btn btn-secondary" onClick={resetConfig}>Reset to Default</button>
                </div>
              </div>
            )}
            {guestTab === 'colors' && (
              <div className="settings-section">
                <h3>Colors & Fonts</h3>
                <div className="form-group">
                  <label>Primary Color (buttons, accents)</label>
                  <input type="color" name="primaryColor" value={config.primaryColor || DEFAULT_PRIMARY} onChange={handleColorChange} />
                  <div style={{display:'flex', alignItems:'center', gap:'10px', marginTop:'4px'}}>
                    <span style={{display:'inline-block', width:'30px', height:'30px', borderRadius:'50%', background: config.primaryColor || DEFAULT_PRIMARY}}></span>
                    <span style={{fontSize:'13px', color:'#999'}}>{config.primaryColor || DEFAULT_PRIMARY}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Accent Color (gold, highlights)</label>
                  <input type="color" name="accentColor" value={config.accentColor || DEFAULT_ACCENT} onChange={handleColorChange} />
                  <div style={{display:'flex', alignItems:'center', gap:'10px', marginTop:'4px'}}>
                    <span style={{display:'inline-block', width:'30px', height:'30px', borderRadius:'50%', background: config.accentColor || DEFAULT_ACCENT}}></span>
                    <span style={{fontSize:'13px', color:'#999'}}>{config.accentColor || DEFAULT_ACCENT}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Font Family</label>
                  <select name="fontFamily" value={config.fontFamily || DEFAULT_FONT} onChange={handleConfigChange} style={{padding:'10px', borderRadius:'10px', border:'1px solid #d4c8b8', width:'100%', fontFamily:'inherit'}}>
                    <option value="'Playfair Display', serif">Playfair Display (Classic)</option>
                    <option value="'Georgia', serif">Georgia (Traditional)</option>
                    <option value="'Times New Roman', serif">Times New Roman (Formal)</option>
                    <option value="'Inter', sans-serif">Inter (Modern)</option>
                    <option value="'Montserrat', sans-serif">Montserrat (Clean)</option>
                  </select>
                </div>
                <div className="mt-16" style={{padding:'16px', background:'#f5f5f5', borderRadius:'12px'}}>
                  <p style={{fontSize:'13px', color:'#666'}}>Preview:</p>
                  <div style={{fontFamily: config.fontFamily || DEFAULT_FONT}}>
                    <p style={{color: config.primaryColor || DEFAULT_PRIMARY, fontSize:'20px', fontWeight:'bold'}}>Primary Color Text</p>
                    <p style={{color: config.accentColor || DEFAULT_ACCENT, fontSize:'18px'}}>Accent Color Text</p>
                    <p style={{fontSize:'16px'}}>Sample text in your chosen font.</p>
                  </div>
                </div>
                <div className="mt-16">
                  <button className="btn btn-secondary" onClick={() => {
                    const updated = { ...config, primaryColor: DEFAULT_PRIMARY, accentColor: DEFAULT_ACCENT, fontFamily: DEFAULT_FONT };
                    saveConfig(updated);
                  }}>Reset Colors & Fonts</button>
                </div>
              </div>
            )}
          </div>
          <div className="settings-actions">
            <button className="settings-close-btn" onClick={() => setShowSettings(false)}>Done</button>
          </div>
        </div>
      </div>
    );
  };

  // ===== Login Modal =====
  const renderLoginModal = () => {
    if (!showLoginModal) return null;
    return (
      <div className="login-overlay">
        <div className="login-modal">
          <h2>Admin Access</h2>
          <p>Please enter the admin password to edit your invitation.</p>
          <form onSubmit={handleLoginSubmit}>
            <input
              type="password"
              placeholder="Enter password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              autoFocus
            />
            {loginError && <p className="login-error">{loginError}</p>}
            <button type="submit">Unlock</button>
          </form>
          <small>You only need to do this once per browser.</small>
        </div>
      </div>
    );
  };

  // ===== Render =====
  const pages = [
    renderCover(),              // 0
    renderClassicInvitation(),  // 1
    renderCeremony(),           // 2
    renderCelebration(),        // 3
    renderLocations(),          // 4
    renderGiftRegistry(),       // 5
    renderRsvp(),               // 6
    renderMedia(),              // 7
    renderAlbumPage(),          // 8
    renderThankYou()            // 9
  ];

  const styleClass = style === 'bw' ? 'theme-bw' : style === 'dark' ? 'theme-dark' : 'theme-light';

  return (
    <HelmetProvider>
      <div 
        className={`app-container ${styleClass}`} 
        onClick={resetUiTimer}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => { pointerIdRef.current = null; }}
        onWheel={handleWheel}
      >
        
        <Helmet>
          <title>{config.couple1} & {config.couple2} | Wedding Invitation</title>
          <meta property="og:title" content={`${config.couple1} & ${config.couple2} | Wedding Invitation`} />
          <meta property="og:description" content={`You are invited to the wedding of ${config.couple1} and ${config.couple2}. We hope you can join us!`} />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:image" content={`${window.location.origin}/pics/cover.jpg`} />
        </Helmet>

        <div className={`ui-wrapper ${!uiVisible ? 'ui-elements-hidden' : ''}`}>
          
          {currentPage !== 0 && (
            <div className="page-counter">
              {Array.from({ length: totalPages }, (_, i) => (
                <div key={i} className={`page-dot ${i === currentPage ? 'active' : ''}`} onClick={() => goToPage(i)} />
              ))}
            </div>
          )}

          <div className="style-selector">
            <button className={`style-btn ${style === 'light' ? 'active' : ''}`} onClick={() => setStyle('light')}>🌿 Light</button>
            <button className={`style-btn ${style === 'dark' ? 'active' : ''}`} onClick={() => setStyle('dark')}>✨ Dark</button>
            <button className={`style-btn ${style === 'bw' ? 'active' : ''}`} onClick={() => setStyle('bw')}>🖤 B&W</button>
          </div>

          <button className="home-btn-top" onClick={() => goToPage(0)}>
            <FontAwesomeIcon icon={faHome} />
          </button>

          <button className="hamburger-btn-top" onClick={() => setShowHamburger(true)}>
            <FontAwesomeIcon icon={faBars} />
          </button>

          <button className="music-btn-always" onClick={toggleMusic} style={{ color: config.accentColor || DEFAULT_ACCENT }}>
            <FontAwesomeIcon icon={faMusic} />
          </button>

          <div className="nav-container">
            <button className="nav-arrow prev" onClick={prevPage} style={{ opacity: currentPage === 0 ? 0.3 : 1, pointerEvents: currentPage === 0 ? 'none' : 'auto' }}>
              <FontAwesomeIcon icon={faChevronUp} />
            </button>
            <button className="nav-arrow next" onClick={nextPage} style={{ opacity: currentPage === totalPages - 1 ? 0.3 : 1, pointerEvents: currentPage === totalPages - 1 ? 'none' : 'auto' }}>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>

          {adminAuthenticated && !guestView && (
            <button className="settings-btn-bottom" onClick={() => setShowSettings(true)} style={{ borderColor: config.accentColor || DEFAULT_ACCENT }}>
              <FontAwesomeIcon icon={faCog} />
            </button>
          )}

        </div>

        <div className="story-container">
          <div className="story-slider" style={{ transform: `translateY(-${currentPage * 100}vh)` }}>
            {pages.map((page, index) => (
              <div key={index} className="story-slide">{page}</div>
            ))}
          </div>
        </div>

        {showHamburger && renderHamburgerMenu()}
        {albumOpen && renderAlbumModal()}
        {showSettings && renderSettings()}
        {renderLoginModal()}

        <audio ref={audioRef} loop preload="auto" crossOrigin="anonymous">
          <source src={config.musicUrl || "/fairouz.mp3"} type="audio/mpeg" />
          <source src="https://cdn.pixabay.com/audio/2022/03/10/audio_c8c8f6b6f2.mp3" type="audio/mpeg" />
        </audio>
      </div>
    </HelmetProvider>
  );
}

export default App;